import { create } from 'zustand';
import { UNASSIGNED_BRANCH } from '@repo/shared-types';
import { readPersistedOrgId, useAuthStore } from './authStore';

export { UNASSIGNED_BRANCH } from '@repo/shared-types';

/** Per-org, because "which branch am I looking at" is an org fact and a user
 *  can belong to two. Same `fos_` prefix as every other key this app owns. */
export function branchStorageKey(orgId: string): string {
  return `fos_branch_${orgId}`;
}

function readStored(orgId: string): string | null {
  try {
    return localStorage.getItem(branchStorageKey(orgId));
  } catch {
    return null; // private mode / blocked site data — "Tất cả" is a safe floor
  }
}

function writeStored(orgId: string, branchId: string | null) {
  try {
    if (branchId) localStorage.setItem(branchStorageKey(orgId), branchId);
    else localStorage.removeItem(branchStorageKey(orgId));
  } catch { /* ignore */ }
}

/**
 * The state the store is BORN with, read synchronously from storage.
 *
 * Hydrating in BranchPicker's effect instead meant the first render of every
 * page reported "Tất cả chi nhánh" and the stored branch only landed one render
 * later: every list query fired twice (once org-wide, once filtered) and the
 * user saw a flash of the wrong branch's data before it corrected itself
 * (THI-137 final review F4). Reading persisted auth here is safe — module init
 * runs long before `hydrateFromStorage()` populates the auth store — and yields
 * `{ null, null }` for a signed-out tab, which is exactly "Tất cả".
 */
export function initialBranchState(): { orgId: string | null; branchId: string | null } {
  const orgId = readPersistedOrgId();
  return { orgId, branchId: orgId ? readStored(orgId) : null };
}

/**
 * What a lens should report for `orgId` given the store's current state.
 *
 * The store is hydrated at creation, so it only disagrees with `orgId` right
 * after a sign-in or an org switch. For that one render this reads THIS org's
 * key straight from storage rather than reporting "Tất cả" and letting
 * `useBranchScope`'s effect correct it a render later. Deliberately pure — no
 * setState during render, which React would (rightly) complain about.
 */
export function activeBranchIdFor(
  state: { orgId: string | null; branchId: string | null },
  orgId: string | null,
): string | undefined {
  if (orgId && state.orgId !== orgId) return readStored(orgId) ?? undefined;
  return state.branchId ?? undefined;
}

/**
 * The branch ids `reconcile` should treat as alive, given the current active
 * branch list and unassigned count.
 *
 * `UNASSIGNED_BRANCH` rides on this list only while BOTH hold: there is
 * something in it (`unassignedSlotCount > 0`) AND there is at least one active
 * branch left to file it under. Once the last active branch is archived, "Chưa
 * gán" has nothing to be filtered against — the scope must heal to "Tất cả"
 * the same way a stale branch id does (final review F2: without the second
 * clause, `?branchId=none` outlived every active branch with no UI able to
 * clear it, since only a real branch id self-heals via this same function).
 */
export function activeBranchIds(branches: { id: string }[], unassignedSlotCount: number): string[] {
  const ids = branches.map((b) => b.id);
  if (unassignedSlotCount > 0 && branches.length > 0) ids.push(UNASSIGNED_BRANCH);
  return ids;
}

interface BranchState {
  orgId: string | null;
  /** null = "Tất cả chi nhánh". A cuid = that branch. The literal
   *  `UNASSIGNED_BRANCH` ('none') = the "Chưa gán chi nhánh" scope — deliberately
   *  just a string the API understands, not a new type, so nothing downstream
   *  has to widen. */
  branchId: string | null;
  select: (orgId: string, branchId: string | null) => void;
  syncOrg: (orgId: string) => void;
  reconcile: (orgId: string, activeIds: string[]) => void;
}

export const useBranchStore = create<BranchState>((set, get) => ({
  ...initialBranchState(),
  select: (orgId, branchId) => { writeStored(orgId, branchId); set({ orgId, branchId }); },
  // Called whenever the signed-in org changes. Reads THAT org's key, so
  // switching orgs never carries a branch id across a tenant boundary.
  syncOrg: (orgId) => { if (get().orgId !== orgId) set({ orgId, branchId: readStored(orgId) }); },
  // A stored id can outlive the branch: it was archived, renamed away, or the
  // org downgraded and deleted it. Reset to "Tất cả" SILENTLY — a toast about a
  // branch the teacher no longer has explains nothing.
  //
  // `UNASSIGNED_BRANCH` is not a branch and will never be in `activeIds` on its
  // own, so it would be reset here on every render. The CALLER decides instead
  // (see `useBranchScope`): it appends 'none' to `activeIds` exactly while
  // `unassignedSlotCount > 0`. That is not a workaround — it is the same
  // self-healing rule this function already implements, applied to a scope that
  // stops existing the moment the last branchless class is assigned.
  reconcile: (orgId, activeIds) => {
    const current = get().branchId;
    if (current && !activeIds.includes(current)) { writeStored(orgId, null); set({ branchId: null }); }
  },
}));

/** What the list hooks want: the selected branch, or `undefined` for "no
 *  filter" — so it drops straight into an opts object. */
export function useActiveBranchId(): string | undefined {
  const orgId = useAuthStore((s) => s.organization?.id) ?? null;
  const storeOrgId = useBranchStore((s) => s.orgId);
  const branchId = useBranchStore((s) => s.branchId);
  return activeBranchIdFor({ orgId: storeOrgId, branchId }, orgId);
}
